import React, { FC, useEffect, useRef, useState } from 'react';
import { Pax } from 'ts/dynamic/interfaces/DynamicBookResponse';
import { useTranslation } from 'react-i18next';
import { dynAgesList, genderOptions, getBookingAgesStr } from 'constants/dynamic';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import { getDynPassengerValidation } from 'utils/dynamic/dynamic';
import { dateFormat } from 'utils/shared/utils';
import PhoneInput from 'components/shared/ui/PhoneInput';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useSearchParams } from 'react-router-dom';

interface DynamicTicketFormProps {
  paxes: Pax[];
  setPaxes: (paxes: Pax[]) => void;
  isSubmitted: boolean;
  maxPaxes: number;
}

const DynamicTicketForm: FC<DynamicTicketFormProps> = ({ isSubmitted, ...props }) => {
  const { t } = useTranslation();
  const firstPax = useRef({ ...props.paxes[0], gender: 'M' });
  const [urlParams] = useSearchParams();
  const [pax, setPax] = useState<Pax>({ ...firstPax.current });
  const ticketPaxCount = useRef(urlParams.get('ticketPaxCount'));

  useEffect(() => {
    if (ticketPaxCount.current) {
      const count = parseInt(ticketPaxCount.current, 10);
      const newPaxes: Pax[] = [];
      for (let i = 0; i < count; i++) {
        newPaxes.push({ ...firstPax.current });
      }
      props.setPaxes(newPaxes);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangePax = (pax: Pax) => {
    const newPaxes = props.paxes.map(() => ({ ...pax }));
    props.setPaxes(newPaxes);
    setPax(pax);
  };

  return (
    <div>
      <div className="book-form-room">
        <div className="title text-md mb-15">
          <span className="mr-5">{t('passenger.passenger')}</span>

          <span className="text-sm text-light text-normal">
            {getBookingAgesStr(pax.paxTypeCode, dynAgesList)}
          </span>
        </div>
        <div className="form-wrapper row">
          <FormGroupSelect
            onChange={(gender) => onChangePax({ ...pax, gender })}
            value={pax.gender}
            label={t('passenger.gender')}
            className="col-md-3 col-12"
            options={genderOptions}
            needToTranslate={true}
          />

          <FormGroup
            name="firstName"
            onChange={(firstName) => onChangePax({ ...pax, firstName })}
            value={pax.firstName}
            label={t('passenger.fn')}
            className="col-md-3 col-12"
            validation={getDynPassengerValidation(0).firstName}
            isSubmitted={isSubmitted}
          />
          <FormGroup
            name="lastName"
            onChange={(lastName) => onChangePax({ ...pax, lastName })}
            value={pax.lastName}
            label={t('passenger.ln')}
            className="col-md-3 col-12"
            validation={getDynPassengerValidation(0).lastName}
            isSubmitted={isSubmitted}
          />
          <FormGroupDatepicker
            label={t('passenger.dob')}
            onChange={(dob) => onChangePax({ ...pax, dob: dateFormat(dob, 'yyyy-MM-dd') })}
            value={pax.dob ? new Date(pax.dob) : new Date()}
            className="col-md-3 col-12"
            validation={getDynPassengerValidation(0).dob}
            isSubmitted={isSubmitted}
          />
        </div>
        <div className="form-wrapper row">
          <PhoneInput
            onChange={(phone) => onChangePax({ ...pax, phone })}
            value={pax.phone}
            isSubmitted={isSubmitted}
            validation={getDynPassengerValidation(0).phone}
            className="col-md-6 col-12"
          />
          <FormGroup
            name="email"
            onChange={(email) => onChangePax({ ...pax, email })}
            value={pax.email}
            label={t('passenger.email')}
            className="col-md-6 col-12"
            validation={getDynPassengerValidation(0).email}
            isSubmitted={isSubmitted}
          />
        </div>
      </div>

      <div></div>
    </div>
  );
};

export default withErrorBoundary(DynamicTicketForm, 'DynamicTicketForm');
